import {
  getVariableValue,
  parseTokens,
  resolveTokens,
} from "@/shared/utils/resolve-tokens";
import { useHandleTextAiPromptMutation } from "@api/ai";
import Button from "@components/ui/button";
import Input from "@components/ui/input";
import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import { IconFileTextAi } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { track } from "@/features/analytics";
import { VARIABLES_EVENTS } from "@features/dynamic-variables/analytics/variables-analytics";

interface Props {
  /** The org AI variable's prompt (`{{tokens}}` allowed). */
  prompt: string;
  /** Values the prompt resolves against: the row (and parent rows) over the
   * top-level version values, plus the row under its group key for the
   * `{{group.field}}` form. */
  scope: Record<string, unknown>;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim().length > 0;

const withInstruction = (prompt: string, instruction: string): string =>
  instruction.trim()
    ? `${prompt}\n\nAdditional instruction: ${instruction.trim()}`
    : prompt;

/**
 * AI field inside a repeatable row: the same behaviour as the top-level AI
 * control (controls/ai-control.tsx) — generates automatically once every
 * variable the prompt references has a value, shows what is still missing,
 * and takes an optional extra instruction — but driven by value/onChange
 * against the ROW's scope instead of a form field name. A value already
 * present (typed, imported, generated earlier) is kept until the inputs or
 * the instruction change.
 */
export const AiCell = ({
  prompt,
  scope,
  value,
  onChange,
  invalid = false,
}: Props) => {
  const [generate] = useHandleTextAiPromptMutation();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const sequenceRef = useRef(0);

  const dependencies = useMemo(
    () =>
      Array.from(
        new Set(
          parseTokens(prompt)
            .filter((segment) => segment.type === "var")
            .map((segment) => (segment.type === "var" ? segment.name : "")),
        ),
      ),
    [prompt],
  );
  const missingDependency = dependencies.find(
    (name) => !hasValue(getVariableValue(scope, name)),
  );
  const ready = !missingDependency;
  const signature = withInstruction(resolveTokens(prompt, scope), instruction);
  // A pre-filled cell is treated as generated for the current inputs, so it
  // isn't overwritten on mount — only when the inputs or instruction change.
  const lastRequestedRef = useRef(hasValue(value) ? signature : "");

  const runGeneration = useCallback(
    async (fullPrompt: string) => {
      if (!fullPrompt.trim() || lastRequestedRef.current === fullPrompt) return;
      lastRequestedRef.current = fullPrompt;
      sequenceRef.current += 1;
      const sequence = sequenceRef.current;
      setStatus("loading");
      const startedAt = Date.now();
      try {
        const result = await generate({
          system:
            "Generate only the final value requested by the prompt. Do not add commentary.",
          prompt: fullPrompt,
        }).unwrap();
        if (sequence !== sequenceRef.current) return;
        track(VARIABLES_EVENTS.GENERATE_AI_VARIABLE, {
          surface: "repeatable row",
          status: "success",
          "duration ms": Date.now() - startedAt,
        });
        onChange(String(result.response ?? ""));
        setStatus("idle");
      } catch {
        if (sequence !== sequenceRef.current) return;
        track(VARIABLES_EVENTS.GENERATE_AI_VARIABLE, {
          surface: "repeatable row",
          status: "failed",
          "duration ms": Date.now() - startedAt,
        });
        // Keep the failed signature: `runGeneration` changes identity with the
        // parent's inline `onChange`, so clearing it here would re-fire the
        // request on every sibling keystroke. "Update" clears it explicitly.
        setStatus("error");
      }
    },
    [generate, onChange],
  );

  useEffect(() => {
    if (!ready || !signature.trim() || lastRequestedRef.current === signature)
      return;
    const timer = window.setTimeout(() => void runGeneration(signature), 600);
    return () => window.clearTimeout(timer);
  }, [ready, signature, runGeneration]);

  useEffect(
    () => () => {
      sequenceRef.current += 1;
    },
    [],
  );

  const updateInstruction = () => {
    const next = draft.trim();
    setInstruction(next);
    setOpen(false);
    lastRequestedRef.current = "";
    void runGeneration(withInstruction(resolveTokens(prompt, scope), next));
  };

  return (
    <div className="tw-flex tw-min-h-9 tw-flex-col tw-items-start tw-justify-center tw-gap-3 tw-px-2 tw-py-1">
      <p className="tw-m-0 tw-text-sm tw-font-normal tw-leading-5 tw-text-blue-500">
        {status === "loading"
          ? "Generating content..."
          : status === "error"
            ? "Generation failed. Update the instruction to retry."
            : value ||
              (missingDependency
                ? `Fill the variable {{${missingDependency}}} to get the AI generated content`
                : "AI content will generate automatically")}
      </p>
      <Popover open={open} onOpenChange={setOpen} placement="bottom-start">
        <Popover.Trigger asChild>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Additional instruction"
            className={cn("tw-h-8 tw-shadow-2xs", open && "tw-bg-blue-50/50")}
          >
            <IconFileTextAi className="tw-mr-1 tw-size-3.5 !tw-fill-none" />
            Additional instruction
          </Button>
        </Popover.Trigger>
        <Popover.Content className="tw-z-[100000] tw-w-[286px] tw-rounded-xl tw-bg-white tw-p-4 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-blue/5">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-blue">
              <IconFileTextAi className="tw-size-4 !tw-fill-none" />
              Additional instruction
            </div>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Additional instruction"
              placeholder="Add an instruction"
            />
            <Button
              variant="secondary"
              className="tw-self-start"
              onClick={updateInstruction}
            >
              Update
            </Button>
          </div>
        </Popover.Content>
      </Popover>
      {invalid && (
        <span className="tw-text-xs tw-text-[#D43548]">Missing value</span>
      )}
    </div>
  );
};
